import React, { useCallback, useEffect, useMemo, useState } from "react";

// Visier Security Runbook: IDN -> Visier group -> permission -> DAS / filters /
// capabilities / content packages -> analyses & guidebooks. Security config only.

const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";

const ART_ORDER = ["analysis", "guidebookTopic", "report", "metric", "dimension", "keyGroup", "planModel", "dataConnector"];
const ART_LABELS = {
  analysis: "Analyses", guidebookTopic: "Guidebook Topics", report: "Reports", metric: "Metrics",
  dimension: "Dimensions", keyGroup: "Key Groups", planModel: "Plan Models", dataConnector: "Data Connectors",
};
const ART_SINGULAR = {
  analysis: "Analysis", guidebookTopic: "Guidebook Topic", report: "Report", metric: "Metric",
  dimension: "Dimension", keyGroup: "Key Group", planModel: "Plan Model", dataConnector: "Data Connector",
};
const LIST_LIMIT = 300;

const SECTIONS = [
  { id: "guide", label: "Runbook Guide" },
  { id: "group", label: "User Groups" },
  { id: "perm", label: "Permissions" },
  { id: "das", label: "Data Access Sets" },
  { id: "sf", label: "Security Filters" },
  { id: "cap", label: "Capabilities" },
  { id: "cp", label: "Content Packages" },
  { id: "guidebook", label: "Guidebooks" },
  { id: "analysis", label: "Analyses" },
  { id: "role", label: "IDN Roles" },
  { id: "ap", label: "IDN Access Profiles" },
  { id: "profile", label: "Visier Profiles" },
];

const TYPE_LABELS = {
  group: "User Group", perm: "Permission", das: "Data Access Set", sf: "Security Filter", cap: "Capability",
  cp: "Content Package", guidebook: "Guidebook", artifact: "Content", role: "IDN Role", ap: "IDN Access Profile",
  profile: "Visier Profile", prop: "Property",
};

function push(map, key, val) {
  if (!map[key]) map[key] = [];
  if (!map[key].includes(val)) map[key].push(val);
}

function byName(a, b) {
  return (a?.name || "").localeCompare(b?.name || "", undefined, { sensitivity: "base" });
}

function walkCriteria(node, fn) {
  if (!node) return;
  if (node.children) node.children.forEach(ch => walkCriteria(ch, fn));
  else fn(node);
}

export const artKey = (type, name, guidebook) => `${type}|${guidebook || ""}|${name}`;

export function buildIndex(d) {
  const ix = {
    permToGroups: {}, dasToPerms: {}, sfToPerms: {}, capToPerms: {}, cpToPerms: {}, propIndex: {},
    artifacts: {}, guidebooks: {}, entByValue: {}, apsByEnt: {}, rolesByAp: {}, rolesByEnt: {},
    rolesByCriteriaValue: {},
  };
  Object.values(d.groups).forEach(g => g.permissionIds.forEach(p => push(ix.permToGroups, p, g.id)));
  Object.values(d.permissions).forEach(p => {
    p.capabilities.forEach(c => push(ix.capToPerms, c, p.id));
    p.contentPackageIds.forEach(c => push(ix.cpToPerms, c, p.id));
    p.dataSecurity.forEach(ds => {
      if (ds.dasId) push(ix.dasToPerms, ds.dasId, p.id);
      ds.securityFilterIds.forEach(f => push(ix.sfToPerms, f, p.id));
      (ds.inlineProperties || []).forEach(([pid, obj, lvl]) => {
        const k = `${obj}.${pid}`;
        if (!ix.propIndex[k]) ix.propIndex[k] = [];
        ix.propIndex[k].push({ kind: "perm", id: p.id, level: lvl });
      });
    });
  });
  Object.values(d.das).forEach(x => x.properties.forEach(([pid, obj, lvl]) => {
    const k = `${obj}.${pid}`;
    if (!ix.propIndex[k]) ix.propIndex[k] = [];
    ix.propIndex[k].push({ kind: "das", id: x.id, level: lvl });
  }));
  Object.values(d.contentPackages).forEach(cp => Object.entries(cp.artifacts).forEach(([t, items]) => {
    items.forEach(([name, gb]) => {
      const k = artKey(t, name, gb);
      if (!ix.artifacts[k]) ix.artifacts[k] = { key: k, type: t, name, guidebook: gb, cps: [] };
      if (!ix.artifacts[k].cps.includes(cp.id)) ix.artifacts[k].cps.push(cp.id);
      if (gb) {
        if (!ix.guidebooks[gb]) ix.guidebooks[gb] = { name: gb, topics: [], cps: [] };
        if (!ix.guidebooks[gb].topics.includes(name)) ix.guidebooks[gb].topics.push(name);
        if (!ix.guidebooks[gb].cps.includes(cp.id)) ix.guidebooks[gb].cps.push(cp.id);
      }
    });
  }));
  Object.values(ix.guidebooks).forEach(g => g.topics.sort((a, b) => a.localeCompare(b)));

  const idn = d.idn;
  Object.values(idn.entitlements).forEach(e => { ix.entByValue[e.value] = e; });
  Object.values(idn.accessProfiles).forEach(a => a.entitlementIds.forEach(e => push(ix.apsByEnt, e, a.id)));
  Object.values(idn.roles).forEach(r => {
    r.accessProfileIds.forEach(a => push(ix.rolesByAp, a, r.id));
    r.entitlementIds.forEach(e => push(ix.rolesByEnt, e, r.id));
    walkCriteria(r.criteria, leaf => {
      if (leaf.visier) (leaf.rawValues || []).forEach(v => push(ix.rolesByCriteriaValue, v, r.id));
    });
  });
  return ix;
}

export function groupsForPerms(ix, permIds) {
  const out = [];
  permIds.forEach(p => (ix.permToGroups[p] || []).forEach(g => { if (!out.includes(g)) out.push(g); }));
  return out;
}

export function groupEffective(d, g) {
  const caps = new Set(), cps = new Set(), das = new Set(), sfs = new Set();
  g.permissionIds.forEach(pid => {
    const p = d.permissions[pid];
    if (!p) return;
    p.capabilities.forEach(c => caps.add(c));
    p.contentPackageIds.forEach(c => cps.add(c));
    p.dataSecurity.forEach(ds => {
      if (ds.dasId) das.add(ds.dasId);
      ds.securityFilterIds.forEach(f => sfs.add(f));
    });
  });
  return { caps: [...caps], cps: [...cps], das: [...das], sfs: [...sfs] };
}

// Every IDN path that provisions a Visier entitlement (group or profile) by its value (Visier ID).
export function idnPathsFor(d, ix, visierId) {
  const ent = ix.entByValue[visierId];
  if (!ent) return null;
  const aps = (ix.apsByEnt[ent.id] || []).map(id => d.idn.accessProfiles[id]).filter(Boolean);
  const viaAp = aps.map(ap => ({ ap, roles: (ix.rolesByAp[ap.id] || []).map(id => d.idn.roles[id]).filter(Boolean) }));
  const direct = (ix.rolesByEnt[ent.id] || []).map(id => d.idn.roles[id]).filter(Boolean);
  const downstream = (ix.rolesByCriteriaValue[visierId] || []).map(id => d.idn.roles[id]).filter(Boolean);
  return { ent, viaAp, direct, downstream };
}

export function buildSearchItems(d, ix) {
  const items = [];
  const add = (type, id, label, sub) => items.push({ type, id, label, sub, l: (label + " " + (sub || "")).toLowerCase() });
  Object.values(d.groups).forEach(x => add("group", x.id, x.name, x.description));
  Object.values(d.permissions).forEach(x => add("perm", x.id, x.name, x.description));
  Object.values(d.das).forEach(x => add("das", x.id, x.name, x.object));
  Object.values(d.securityFilters).forEach(x => add("sf", x.id, x.name, x.object));
  Object.entries(d.capabilities).forEach(([k, [dn]]) => add("cap", k, dn, k));
  Object.values(d.contentPackages).forEach(x => add("cp", x.id, x.name, ""));
  Object.values(ix.guidebooks).forEach(x => add("guidebook", x.name, x.name, `${x.topics.length} topics`));
  Object.values(ix.artifacts).forEach(x => add("artifact", x.key, x.name, ART_SINGULAR[x.type] + (x.guidebook ? ` · ${x.guidebook}` : "")));
  Object.values(d.idn.roles).forEach(x => add("role", x.id, x.name, ""));
  Object.values(d.idn.accessProfiles).forEach(x => add("ap", x.id, x.name, ""));
  Object.values(d.profiles).forEach(x => add("profile", x.id, x.name, ""));
  Object.keys(ix.propIndex).forEach(k => add("prop", k, k, "Property"));
  return items;
}

export function entityName(d, ix, type, id) {
  switch (type) {
    case "group": return d.groups[id]?.name;
    case "perm": return d.permissions[id]?.name;
    case "das": return d.das[id]?.name;
    case "sf": return d.securityFilters[id]?.name;
    case "cap": return d.capabilities[id]?.[0] || id;
    case "cp": return d.contentPackages[id]?.name;
    case "guidebook": return id;
    case "artifact": return ix.artifacts[id]?.name;
    case "role": return d.idn.roles[id]?.name;
    case "ap": return d.idn.accessProfiles[id]?.name;
    case "profile": return d.profiles[id]?.name;
    case "prop": return id;
    default: return id;
  }
}

// ── Shared UI ────────────────────────────────────────────────────────────────

function Link({ nav, type, id, children, title }) {
  return (
    <button type="button" className="vrb-link" title={title} onClick={() => nav({ type, id })}>
      {children}
    </button>
  );
}

function Badge({ tone = "muted", children, title }) {
  return <span className={`vrb-badge vrb-badge-${tone}`} title={title}>{children}</span>;
}

function LayerBadge({ layer }) {
  return layer === "NonVersioned"
    ? <Badge tone="green" title="NonVersioned: live in production immediately">Production (NonVersioned)</Badge>
    : <Badge tone="blue" title="Versioned: part of the published production version">Versioned</Badge>;
}

function Block({ title, count, children, hint }) {
  return (
    <section className="vrb-block">
      <div className="vrb-block-title">
        {title}{count !== undefined && <span className="vrb-count">{count}</span>}
      </div>
      {hint && <div className="vrb-hint">{hint}</div>}
      {children}
    </section>
  );
}

function Empty({ children }) {
  return <div className="vrb-empty">{children}</div>;
}

function LinkList({ nav, type, ids, d, ix, empty = "None" }) {
  const rows = ids.map(id => ({ id, name: entityName(d, ix, type, id) || id }))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  if (!rows.length) return <Empty>{empty}</Empty>;
  return (
    <div className="vrb-chips">
      {rows.map(r => <Link key={r.id} nav={nav} type={type} id={r.id}>{r.name}</Link>)}
    </div>
  );
}

function FilterList({ items, render, placeholder = "Filter…", limit = LIST_LIMIT, getText }) {
  const [q, setQ] = useState("");
  const terms = q.toLowerCase().split(/\s+/).filter(Boolean);
  const filtered = terms.length
    ? items.filter(it => { const t = getText(it).toLowerCase(); return terms.every(x => t.includes(x)); })
    : items;
  return (
    <div>
      <input className="cug-input vrb-filter" value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder} />
      <div className="vrb-filter-meta">
        {filtered.length} of {items.length}{filtered.length > limit && ` · showing first ${limit}, refine the filter`}
      </div>
      {filtered.slice(0, limit).map(render)}
    </div>
  );
}

function Criteria({ node, d, nav }) {
  if (!node) return null;
  if (node.op === "AND" || node.op === "OR") {
    return (
      <div className="vrb-crit-group">
        <div className="vrb-crit-op">{node.op === "AND" ? "ALL of the following (AND)" : "ANY of the following (OR)"}</div>
        {node.children.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <div className="vrb-crit-join">{node.op}</div>}
            <Criteria node={c} d={d} nav={nav} />
          </React.Fragment>
        ))}
      </div>
    );
  }
  const values = node.values.map((v, i) => {
    const raw = node.rawValues?.[i];
    if (node.visier && raw && d.groups[raw]) return <Link key={i} nav={nav} type="group" id={raw}>{v}</Link>;
    if (node.visier && raw && d.profiles[raw]) return <Link key={i} nav={nav} type="profile" id={raw}>{v}</Link>;
    return <code key={i} className="vrb-val">{v}</code>;
  });
  return (
    <div className="vrb-crit-leaf">
      <span className="vrb-crit-type">{node.type}</span>
      {node.source && <span className="vrb-crit-src">{node.source}</span>}
      {node.type !== "Entitlement" && <span className="vrb-crit-attr">{node.attribute}</span>}
      <span className="vrb-crit-opname">{node.op}</span>
      <span className="vrb-crit-vals">{values}</span>
    </div>
  );
}

function Approvals({ list }) {
  if (!list || !list.length) return <span className="vrb-muted">No approval required</span>;
  return <span>{list.join(" → ")}</span>;
}

function RoleCard({ role, d, nav, compact }) {
  const how = role.criteria
    ? "Automatically assigned when the criteria below match"
    : role.hasIdentityList ? "Assigned to a fixed list of identities"
    : role.requestable ? "Assigned only by access request" : "Assigned manually (no criteria, not requestable)";
  return (
    <div className="vrb-card">
      <div className="vrb-card-head">
        <Link nav={nav} type="role" id={role.id}>{role.name}</Link>
        <Badge tone="purple">IDN Role</Badge>
        {!role.enabled && <Badge tone="red">Disabled</Badge>}
        {role.requestable && <Badge tone="amber">Requestable</Badge>}
      </div>
      <div className="vrb-kv">
        <span>How assigned</span><span>{how}</span>
        {role.requestable && <><span>Request approvals</span><span><Approvals list={role.approvals} /></span></>}
        <span>Owner</span><span>{role.owner || "—"}</span>
      </div>
      {role.criteria && !compact && <Criteria node={role.criteria} d={d} nav={nav} />}
    </div>
  );
}

function ApCard({ ap, nav, children }) {
  return (
    <div className="vrb-card">
      <div className="vrb-card-head">
        <Link nav={nav} type="ap" id={ap.id}>{ap.name}</Link>
        <Badge tone="blue">IDN Access Profile</Badge>
        {!ap.enabled && <Badge tone="red">Disabled</Badge>}
        {ap.requestable ? <Badge tone="amber">Requestable</Badge> : <Badge>Not requestable</Badge>}
      </div>
      <div className="vrb-kv">
        {ap.requestable && <><span>Request approvals</span><span><Approvals list={ap.approvals} /></span></>}
        <span>Owner</span><span>{ap.owner || "—"}</span>
      </div>
      {children}
    </div>
  );
}

// "How users get into this Visier group/profile" — the IDN provisioning chain.
function IdnPath({ d, ix, nav, visierId, kind }) {
  const path = idnPathsFor(d, ix, visierId);
  if (!path) {
    return (
      <div className="vrb-warn">
        This {kind} is <b>not provisioned by IdentityNow</b> (no entitlement on <i>{d.idn.sourceName}</i>).
        Membership is maintained directly in Visier.
      </div>
    );
  }
  const { ent, viaAp, direct } = path;
  return (
    <div>
      <div className="vrb-card vrb-card-ent">
        <div className="vrb-card-head">
          <code className="vrb-val">{ent.name}</code>
          <Badge tone="teal">IDN Entitlement · {d.idn.sourceName}</Badge>
          {ent.requestable && <Badge tone="amber">Requestable directly</Badge>}
          {ent.privileged && <Badge tone="red">Privileged</Badge>}
        </div>
        <div className="vrb-hint">Anyone holding this entitlement in IDN is provisioned into the {kind} in Visier.</div>
      </div>
      {viaAp.length === 0 && direct.length === 0 && (
        <div className="vrb-warn">No access profile or role grants this entitlement — it can only be assigned directly{ent.requestable ? " by request" : " by an admin"}.</div>
      )}
      {viaAp.map(({ ap, roles }) => (
        <div key={ap.id} className="vrb-chain">
          <div className="vrb-arrow">▲ granted by</div>
          <ApCard ap={ap} nav={nav}>
            {roles.length === 0
              ? <div className="vrb-hint">Not bundled into any role{ap.requestable ? " — users request this access profile directly." : "."}</div>
              : roles.map(r => (
                <div key={r.id} className="vrb-chain vrb-nested">
                  <div className="vrb-arrow">▲ which is included in</div>
                  <RoleCard role={r} d={d} nav={nav} />
                </div>
              ))}
          </ApCard>
        </div>
      ))}
      {direct.map(r => (
        <div key={r.id} className="vrb-chain">
          <div className="vrb-arrow">▲ granted directly by role</div>
          <RoleCard role={r} d={d} nav={nav} />
        </div>
      ))}
    </div>
  );
}

function describeDimFilter(f) {
  if (f.dynamicDimensionFilter) {
    const x = f.dynamicDimensionFilter;
    const maps = (x.dynamicPropertyMappings || []).map(m =>
      `"${m.hierarchyPropertyId}" matches the user's ${m.userProperty?.name || JSON.stringify(m.userProperty)}`);
    return { dim: x.dimensionId, kind: "Dynamic (per user)", text: maps.join("; ") };
  }
  if (f.staticDimensionFilter) {
    const x = f.staticDimensionFilter;
    const members = (x.memberSelections || []).map(m =>
      (m.namePath || m.name || []).join?.(" › ") + (m.excluded ? " (excluded)" : ""));
    return { dim: x.dimensionId, kind: "Static members", text: members.join(", ") || "—" };
  }
  if (f.memberSelectionFilter || f.selectionConceptReference) {
    return { dim: "", kind: "Concept / selection", text: JSON.stringify(f) };
  }
  return { dim: "", kind: "Other", text: JSON.stringify(f) };
}

function PropertyTable({ rows, nav }) {
  return (
    <FilterList
      items={rows}
      getText={r => `${r[0]} ${r[1]} ${r[2]}`}
      placeholder="Filter properties (e.g. Salary, Detailed)…"
      render={(r, i) => (
        <div key={i} className="vrb-prop-row">
          <Link nav={nav} type="prop" id={`${r[1]}.${r[0]}`}>{r[0]}</Link>
          <span className="vrb-muted">{r[1]}</span>
          <Badge tone={r[2] === "Detailed" ? "green" : r[2] === "Aggregate" ? "amber" : "muted"}>{r[2]}</Badge>
        </div>
      )}
    />
  );
}

function DataSecurity({ perm, d, nav }) {
  if (!perm.dataSecurity.length) return <Empty>No data security profiles (this permission grants no data).</Empty>;
  return (
    <div className="vrb-table">
      <div className="vrb-tr vrb-th"><span>Analytic object</span><span>Which fields (property set)</span><span>Which rows (population)</span></div>
      {perm.dataSecurity.map((ds, i) => (
        <div key={i} className="vrb-tr">
          <span><b>{ds.object}</b></span>
          <span>
            {ds.dasId
              ? (d.das[ds.dasId]
                ? <><Link nav={nav} type="das" id={ds.dasId}>{d.das[ds.dasId].name}</Link> <span className="vrb-muted">({d.das[ds.dasId].properties.length} properties)</span></>
                : <span className="vrb-muted">Unknown DAS {ds.dasId}</span>)
              : ds.inlineProperties?.length
                ? <details><summary>Inline set · {ds.inlineProperties.length} properties</summary><PropertyTable rows={ds.inlineProperties} nav={nav} /></details>
                : <span className="vrb-muted">None defined</span>}
          </span>
          <span>
            {ds.allDataPointAccess && <Badge tone="green">All data points (no row filter)</Badge>}
            {ds.securityFilterIds.map(f => (
              <div key={f}><Link nav={nav} type="sf" id={f}>{d.securityFilters[f]?.name || f}</Link></div>
            ))}
            {!ds.allDataPointAccess && !ds.securityFilterIds.length && <span className="vrb-muted">No row filter in this permission</span>}
            {ds.otherConfig && Object.keys(ds.otherConfig).length > 0 && (
              <details><summary>Other config</summary><pre className="vrb-pre">{JSON.stringify(ds.otherConfig, null, 2)}</pre></details>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

function CapabilityChips({ caps, d, nav }) {
  if (!caps.length) return <Empty>No capabilities</Empty>;
  return (
    <div className="vrb-chips">
      {[...caps].sort((a, b) => (d.capabilities[a]?.[0] || a).localeCompare(d.capabilities[b]?.[0] || b)).map(c => (
        <Link key={c} nav={nav} type="cap" id={c} title={d.capabilities[c]?.[1] || c}>{d.capabilities[c]?.[0] || c}</Link>
      ))}
    </div>
  );
}

function artifactCounts(cp) {
  return ART_ORDER.filter(t => cp.artifacts[t]?.length).map(t => `${cp.artifacts[t].length} ${ART_LABELS[t].toLowerCase()}`).join(" · ");
}

function GuidebookList({ topics, nav, ix }) {
  // topics: [name, guidebook][]
  const books = {};
  topics.forEach(([name, gb]) => { (books[gb] = books[gb] || []).push(name); });
  const names = Object.keys(books).sort();
  if (!names.length) return <Empty>No guidebooks</Empty>;
  return names.map(gb => (
    <details key={gb} className="vrb-details">
      <summary><Link nav={nav} type="guidebook" id={gb}>{gb}</Link> <span className="vrb-muted">({books[gb].length} topics)</span></summary>
      <div className="vrb-chips">
        {books[gb].map(t => <Link key={t} nav={nav} type="artifact" id={artKey("guidebookTopic", t, gb)}>{t}</Link>)}
      </div>
    </details>
  ));
}

function ArtifactBrowser({ artifacts, nav, ix }) {
  const types = ART_ORDER.filter(t => artifacts[t]?.length);
  const [tab, setTab] = useState(types[0]);
  if (!types.length) return <Empty>No content in this package</Empty>;
  const active = types.includes(tab) ? tab : types[0];
  return (
    <div>
      <div className="vrb-pills">
        {types.map(t => (
          <button key={t} type="button" className={`vrb-pill ${active === t ? "active" : ""}`} onClick={() => setTab(t)}>
            {ART_LABELS[t]} <span className="vrb-count">{artifacts[t].length}</span>
          </button>
        ))}
      </div>
      {active === "guidebookTopic"
        ? <GuidebookList topics={artifacts[active]} nav={nav} ix={ix} />
        : (
          <FilterList
            key={active}
            items={artifacts[active]}
            getText={x => x[0] || ""}
            placeholder={`Filter ${ART_LABELS[active].toLowerCase()}…`}
            render={(x, i) => (
              <div key={i} className="vrb-row">
                <Link nav={nav} type="artifact" id={artKey(active, x[0], x[1])}>{x[0]}</Link>
              </div>
            )}
          />
        )}
    </div>
  );
}

// Group -> via permission -> via content package, for "who can see X" questions.
function AccessTrace({ d, ix, nav, cpIds }) {
  const rows = [];
  cpIds.forEach(cpId => (ix.cpToPerms[cpId] || []).forEach(pid => (ix.permToGroups[pid] || []).forEach(gid => {
    rows.push({ gid, pid, cpId });
  })));
  rows.sort((a, b) => (d.groups[a.gid]?.name || "").localeCompare(d.groups[b.gid]?.name || ""));
  if (!rows.length) return <Empty>No user group currently receives this through a permission.</Empty>;
  return (
    <div className="vrb-table">
      <div className="vrb-tr vrb-th"><span>User group</span><span>via Permission</span><span>via Content Package</span></div>
      {rows.map((r, i) => (
        <div key={i} className="vrb-tr">
          <span><Link nav={nav} type="group" id={r.gid}>{d.groups[r.gid]?.name}</Link></span>
          <span><Link nav={nav} type="perm" id={r.pid}>{d.permissions[r.pid]?.name}</Link></span>
          <span><Link nav={nav} type="cp" id={r.cpId}>{d.contentPackages[r.cpId]?.name}</Link></span>
        </div>
      ))}
    </div>
  );
}

function PermGroupTable({ d, ix, nav, permIds }) {
  const rows = [];
  permIds.forEach(pid => (ix.permToGroups[pid] || []).forEach(gid => rows.push({ pid, gid })));
  if (!permIds.length) return <Empty>Not used by any permission.</Empty>;
  return (
    <div className="vrb-table">
      <div className="vrb-tr vrb-th vrb-tr-2"><span>Permission</span><span>User groups with this permission</span></div>
      {permIds.map(pid => (
        <div key={pid} className="vrb-tr vrb-tr-2">
          <span><Link nav={nav} type="perm" id={pid}>{d.permissions[pid]?.name || pid}</Link></span>
          <span><LinkList nav={nav} type="group" ids={ix.permToGroups[pid] || []} d={d} ix={ix} empty="Not assigned to any group" /></span>
        </div>
      ))}
    </div>
  );
}

// ── Detail views ─────────────────────────────────────────────────────────────

function Header({ kind, title, badges, description, id }) {
  return (
    <div className="vrb-header">
      <div className="vrb-kind">{kind}</div>
      <h2 className="vrb-title">{title}</h2>
      <div className="vrb-badges">{badges}</div>
      {description && <div className="vrb-desc">{description}</div>}
      {id && <div className="vrb-id">ID: <code>{id}</code></div>}
    </div>
  );
}

function GroupView({ d, ix, nav, id }) {
  const g = d.groups[id];
  const eff = useMemo(() => groupEffective(d, g), [d, g]);
  const path = idnPathsFor(d, ix, id);
  const m = g.membership;
  const topics = [], analyses = [];
  eff.cps.forEach(cpId => {
    const cp = d.contentPackages[cpId];
    (cp?.artifacts.guidebookTopic || []).forEach(t => topics.push(t));
    (cp?.artifacts.analysis || []).forEach(a => analyses.push(a));
  });
  const uniq = arr => { const s = new Set(); return arr.filter(x => { const k = x.join("|"); if (s.has(k)) return false; s.add(k); return true; }); };
  const uTopics = uniq(topics), uAnalyses = uniq(analyses).sort((a, b) => a[0].localeCompare(b[0]));
  const guidebookCount = new Set(uTopics.map(t => t[1])).size;

  return (
    <div>
      <Header kind="Visier User Group" title={g.name} id={g.id} description={g.description}
        badges={<><LayerBadge layer={g.layer} />{path && <Badge tone="teal">Provisioned by IDN</Badge>}</>} />

      <Block title="1 · How users get into this group">
        <div className="vrb-kv vrb-kv-wide">
          <span>Visier membership type</span>
          <span>
            {m.includeAllUsers ? "All users (includeAllUsers)" : "Explicit membership"}
            {" · "}<b>{m.manualIncludeCount}</b> users included{m.manualExcludeCount ? <> · <b>{m.manualExcludeCount}</b> explicitly excluded</> : null}
          </span>
          {Object.keys(m.rules || {}).length > 0 && (<><span>Rule-based membership</span><span><pre className="vrb-pre">{JSON.stringify(m.rules, null, 2)}</pre></span></>)}
        </div>
        <IdnPath d={d} ix={ix} nav={nav} visierId={id} kind="group" />
      </Block>

      {path?.downstream?.length > 0 && (
        <Block title="Membership in this group also triggers" count={path.downstream.length}
          hint="These IDN roles use this group's entitlement in their assignment criteria.">
          {path.downstream.map(r => <RoleCard key={r.id} role={r} d={d} nav={nav} compact />)}
        </Block>
      )}

      <Block title="2 · Permissions assigned to this group" count={g.permissionIds.length}>
        {g.permissionIds.length === 0 && <Empty>No permissions assigned.</Empty>}
        {g.permissionIds.map(pid => {
          const p = d.permissions[pid];
          if (!p) return <div key={pid} className="vrb-card vrb-muted">Unknown permission {pid}</div>;
          return (
            <details key={pid} className="vrb-card vrb-details">
              <summary className="vrb-card-head">
                <Link nav={nav} type="perm" id={pid}>{p.name}</Link>
                <span className="vrb-muted">
                  {p.dataSecurity.length} data profiles · {p.capabilities.length} capabilities · {p.contentPackageIds.length} content packages
                </span>
              </summary>
              {p.description && <div className="vrb-desc">{p.description}</div>}
              <div className="vrb-sub">Data access</div>
              <DataSecurity perm={p} d={d} nav={nav} />
              <div className="vrb-sub">Capabilities</div>
              <CapabilityChips caps={p.capabilities} d={d} nav={nav} />
              <div className="vrb-sub">Content packages</div>
              <LinkList nav={nav} type="cp" ids={p.contentPackageIds} d={d} ix={ix} empty="No content packages" />
            </details>
          );
        })}
      </Block>

      <Block title="3 · Effective access (union of all permissions)">
        <div className="vrb-sub">Data Access Sets ({eff.das.length})</div>
        <LinkList nav={nav} type="das" ids={eff.das} d={d} ix={ix} />
        <div className="vrb-sub">Security Filters ({eff.sfs.length})</div>
        <LinkList nav={nav} type="sf" ids={eff.sfs} d={d} ix={ix} />
        <div className="vrb-sub">Capabilities ({eff.caps.length})</div>
        <CapabilityChips caps={eff.caps} d={d} nav={nav} />
        <div className="vrb-sub">Content Packages ({eff.cps.length})</div>
        <LinkList nav={nav} type="cp" ids={eff.cps} d={d} ix={ix} />
      </Block>

      <Block title="4 · Guidebooks & analyses available to this group">
        <div className="vrb-sub">Guidebooks ({guidebookCount}) · {uTopics.length} topics</div>
        <GuidebookList topics={uTopics} nav={nav} ix={ix} />
        <div className="vrb-sub">Analyses ({uAnalyses.length})</div>
        {uAnalyses.length ? (
          <FilterList items={uAnalyses} getText={x => x[0]} placeholder="Filter analyses…"
            render={(x, i) => <div key={i} className="vrb-row"><Link nav={nav} type="artifact" id={artKey("analysis", x[0], x[1])}>{x[0]}</Link></div>} />
        ) : <Empty>No analyses</Empty>}
      </Block>
    </div>
  );
}

function PermView({ d, ix, nav, id }) {
  const p = d.permissions[id];
  const cps = p.contentPackageIds.map(c => d.contentPackages[c]).filter(Boolean);
  return (
    <div>
      <Header kind="Visier Permission" title={p.name} id={p.id} description={p.description} badges={<LayerBadge layer={p.layer} />} />
      <Block title="Assigned to user groups" count={(ix.permToGroups[id] || []).length}>
        <LinkList nav={nav} type="group" ids={ix.permToGroups[id] || []} d={d} ix={ix} empty="Not assigned to any group" />
      </Block>
      <Block title="Data access" hint="Fields come from the Data Access Set; rows (population) come from security filters.">
        <DataSecurity perm={p} d={d} nav={nav} />
      </Block>
      <Block title="Capabilities" count={p.capabilities.length}>
        <CapabilityChips caps={p.capabilities} d={d} nav={nav} />
      </Block>
      <Block title="Content packages" count={cps.length}>
        {cps.length === 0 && <Empty>No content packages</Empty>}
        {cps.map(cp => (
          <div key={cp.id} className="vrb-row">
            <Link nav={nav} type="cp" id={cp.id}>{cp.name}</Link> <span className="vrb-muted">{artifactCounts(cp)}</span>
          </div>
        ))}
      </Block>
    </div>
  );
}

function DasView({ d, ix, nav, id }) {
  const x = d.das[id];
  return (
    <div>
      <Header kind="Data Access Set" title={x.name} id={x.id} badges={<><LayerBadge layer={x.layer} /><Badge>{x.object}</Badge></>} />
      <Block title="Used by" hint="Permissions that reference this DAS, and the groups holding them.">
        <PermGroupTable d={d} ix={ix} nav={nav} permIds={ix.dasToPerms[id] || []} />
      </Block>
      <Block title="Properties" count={x.properties.length}>
        <PropertyTable rows={x.properties} nav={nav} />
      </Block>
    </div>
  );
}

function SfView({ d, ix, nav, id }) {
  const x = d.securityFilters[id];
  return (
    <div>
      <Header kind="Security Filter (population / row-level access)" title={x.name} id={x.id}
        badges={<><LayerBadge layer={x.layer} /><Badge>{x.object}</Badge></>} />
      <Block title="Filter definition" count={x.dimensionFilters.length}>
        {x.dimensionFilters.length === 0 && <Empty>No dimension filters.</Empty>}
        {x.dimensionFilters.map((f, i) => {
          const s = describeDimFilter(f);
          return (
            <div key={i} className="vrb-card">
              <div className="vrb-card-head"><b>{s.dim || "Filter"}</b><Badge tone="blue">{s.kind}</Badge></div>
              <div>{s.text}</div>
              <details><summary className="vrb-muted">Raw definition</summary><pre className="vrb-pre">{JSON.stringify(f, null, 2)}</pre></details>
            </div>
          );
        })}
      </Block>
      <Block title="Used by">
        <PermGroupTable d={d} ix={ix} nav={nav} permIds={ix.sfToPerms[id] || []} />
      </Block>
    </div>
  );
}

function CapView({ d, ix, nav, id }) {
  const [dn, desc] = d.capabilities[id] || [id, ""];
  return (
    <div>
      <Header kind="Capability" title={dn} description={desc !== dn ? desc : ""} id={id} />
      <Block title="Granted by">
        <PermGroupTable d={d} ix={ix} nav={nav} permIds={ix.capToPerms[id] || []} />
      </Block>
    </div>
  );
}

function CpView({ d, ix, nav, id }) {
  const cp = d.contentPackages[id];
  return (
    <div>
      <Header kind="Content Package" title={cp.name} id={cp.id} description={cp.description} badges={<Badge>{artifactCounts(cp) || "empty"}</Badge>} />
      <Block title="Granted by">
        <PermGroupTable d={d} ix={ix} nav={nav} permIds={ix.cpToPerms[id] || []} />
      </Block>
      <Block title="Contents">
        <ArtifactBrowser artifacts={cp.artifacts} nav={nav} ix={ix} />
      </Block>
    </div>
  );
}

function GuidebookView({ d, ix, nav, id }) {
  const gb = ix.guidebooks[id];
  return (
    <div>
      <Header kind="Guidebook" title={gb.name} badges={<Badge>{gb.topics.length} topics</Badge>} />
      <Block title="Who can see this guidebook">
        <AccessTrace d={d} ix={ix} nav={nav} cpIds={gb.cps} />
      </Block>
      <Block title="In content packages" count={gb.cps.length}>
        <LinkList nav={nav} type="cp" ids={gb.cps} d={d} ix={ix} />
      </Block>
      <Block title="Topics" count={gb.topics.length}>
        <div className="vrb-chips">
          {gb.topics.map(t => <Link key={t} nav={nav} type="artifact" id={artKey("guidebookTopic", t, gb.name)}>{t}</Link>)}
        </div>
      </Block>
    </div>
  );
}

function ArtifactView({ d, ix, nav, id }) {
  const a = ix.artifacts[id];
  return (
    <div>
      <Header kind={ART_SINGULAR[a.type] || a.type} title={a.name}
        badges={a.guidebook && <Link nav={nav} type="guidebook" id={a.guidebook}>Guidebook: {a.guidebook}</Link>} />
      <Block title="Who can see this">
        <AccessTrace d={d} ix={ix} nav={nav} cpIds={a.cps} />
      </Block>
      <Block title="In content packages" count={a.cps.length}>
        <LinkList nav={nav} type="cp" ids={a.cps} d={d} ix={ix} />
      </Block>
    </div>
  );
}

function PropView({ d, ix, nav, id }) {
  const refs = ix.propIndex[id] || [];
  const rows = [];
  refs.forEach(r => {
    const perms = r.kind === "das" ? (ix.dasToPerms[r.id] || []) : [r.id];
    perms.forEach(pid => (ix.permToGroups[pid] || [null]).forEach(gid => rows.push({ ...r, pid, gid })));
  });
  return (
    <div>
      <Header kind="Data Property" title={id} />
      <Block title="Who can see this field" hint="Row-level population still depends on the permission's security filters.">
        <div className="vrb-table">
          <div className="vrb-tr vrb-th vrb-tr-4"><span>User group</span><span>Permission</span><span>Through</span><span>Access level</span></div>
          {rows.map((r, i) => (
            <div key={i} className="vrb-tr vrb-tr-4">
              <span>{r.gid ? <Link nav={nav} type="group" id={r.gid}>{d.groups[r.gid]?.name}</Link> : <span className="vrb-muted">No group</span>}</span>
              <span><Link nav={nav} type="perm" id={r.pid}>{d.permissions[r.pid]?.name}</Link></span>
              <span>{r.kind === "das" ? <Link nav={nav} type="das" id={r.id}>{d.das[r.id]?.name}</Link> : "Inline property set"}</span>
              <span><Badge tone={r.level === "Detailed" ? "green" : "amber"}>{r.level}</Badge></span>
            </div>
          ))}
        </div>
      </Block>
    </div>
  );
}

function RoleView({ d, ix, nav, id }) {
  const r = d.idn.roles[id];
  const grants = [];
  r.accessProfileIds.forEach(apId => (d.idn.accessProfiles[apId]?.entitlementIds || []).forEach(e => grants.push(e)));
  r.entitlementIds.forEach(e => grants.push(e));
  return (
    <div>
      <Header kind="IdentityNow Role" title={r.name} id={r.id} description={r.description}
        badges={<>{!r.enabled && <Badge tone="red">Disabled</Badge>}{r.requestable && <Badge tone="amber">Requestable</Badge>}<Badge>{r.membershipType || "No criteria"}</Badge></>} />
      <Block title="Define Assignment">
        <RoleCard role={r} d={d} nav={nav} />
      </Block>
      <Block title="Access profiles in this role" count={r.accessProfileNames.length}>
        <div className="vrb-chips">
          {r.accessProfileIds.map((apId, i) => d.idn.accessProfiles[apId]
            ? <Link key={apId} nav={nav} type="ap" id={apId}>{d.idn.accessProfiles[apId].name}</Link>
            : <code key={apId} className="vrb-val">{r.accessProfileNames[i]}</code>)}
        </div>
      </Block>
      <Block title="Visier access granted" count={grants.length}>
        <EntitlementTargets d={d} nav={nav} entIds={grants} />
      </Block>
    </div>
  );
}

function EntitlementTargets({ d, nav, entIds }) {
  if (!entIds.length) return <Empty>No Visier entitlements.</Empty>;
  return (
    <div className="vrb-chips">
      {entIds.map(e => {
        const ent = d.idn.entitlements[e];
        if (!ent) return null;
        if (d.groups[ent.value]) return <Link key={e} nav={nav} type="group" id={ent.value}>Group: {ent.name}</Link>;
        if (d.profiles[ent.value]) return <Link key={e} nav={nav} type="profile" id={ent.value}>Profile: {ent.name}</Link>;
        return <code key={e} className="vrb-val">{ent.attribute}: {ent.name}</code>;
      })}
    </div>
  );
}

function ApView({ d, ix, nav, id }) {
  const ap = d.idn.accessProfiles[id];
  const roles = (ix.rolesByAp[id] || []).map(r => d.idn.roles[r]).filter(Boolean);
  return (
    <div>
      <Header kind="IdentityNow Access Profile" title={ap.name} id={ap.id} description={ap.description} />
      <Block title="Request settings"><ApCard ap={ap} nav={nav} /></Block>
      <Block title="Visier access granted" count={ap.entitlementIds.length}>
        <EntitlementTargets d={d} nav={nav} entIds={ap.entitlementIds} />
      </Block>
      <Block title="Included in roles" count={roles.length}>
        {roles.length === 0 && <Empty>Not included in any role.</Empty>}
        {roles.map(r => <RoleCard key={r.id} role={r} d={d} nav={nav} />)}
      </Block>
    </div>
  );
}

function ProfileView({ d, ix, nav, id }) {
  const p = d.profiles[id];
  return (
    <div>
      <Header kind="Visier Profile (application capabilities)" title={p.name} id={p.id} />
      <Block title="How users get this profile">
        <IdnPath d={d} ix={ix} nav={nav} visierId={id} kind="profile" />
      </Block>
      <Block title="Profile capabilities" count={p.capabilities.length}>
        <div className="vrb-table">
          <div className="vrb-tr vrb-th"><span>Capability</span><span>Access level</span><span>View level</span></div>
          {p.capabilities.map(([c, a, v]) => (
            <div key={c} className="vrb-tr"><span>{c}</span><span>{a}</span><span className="vrb-muted">{v}</span></div>
          ))}
        </div>
      </Block>
    </div>
  );
}

// ── Guide, lists, search ─────────────────────────────────────────────────────

const RECIPES = [
  {
    q: "How did a person get into a Visier group?",
    steps: [
      "Open User Groups and select the group.",
      "Section 1 shows the IDN entitlement that provisions the group, the access profile(s) that contain it, and every role that includes those access profiles.",
      "For dynamic roles, the Define Assignment criteria show exactly which Infor groups / identity attributes put the person in the role. Compare those against the person's identity in IDN.",
      "If the access profile or role is requestable, the person may have requested it — check their access request history (Access Request History page).",
      "If the group is marked “not provisioned by IdentityNow”, members are added directly in Visier.",
    ],
  },
  {
    q: "What permissions are assigned to a group?",
    steps: ["Open User Groups → the group → Section 2. Expand a permission to see its data access, capabilities and content packages inline."],
  },
  {
    q: "What data can a group see (fields and population)?",
    steps: [
      "Group → Section 2 → each permission's Data access table.",
      "“Which fields” is the Data Access Set (open it for the full property list and access level).",
      "“Which rows” is the security filter (e.g. Base Population Access) or “All data points”.",
      "Section 3 shows the union across all of the group's permissions.",
    ],
  },
  {
    q: "What guidebooks / analyses does a user in a group have?",
    steps: ["Group → Section 4 lists every guidebook (with topics) and analysis delivered through the group's content packages."],
  },
  {
    q: "Which groups can see a specific analysis, guidebook or field?",
    steps: [
      "Type its name in the search box at the top (analyses, guidebooks, guidebook topics, metrics and property names are all searchable).",
      "The detail page traces it back: User group → Permission → Content Package (or Data Access Set for fields).",
    ],
  },
  {
    q: "Who has a capability (e.g. Export to Excel)?",
    steps: ["Capabilities → select it. You'll see every permission granting it and the groups holding those permissions."],
  },
  {
    q: "What does an IDN role give in Visier?",
    steps: ["IDN Roles → select the role. It shows the assignment criteria, its access profiles, and the Visier groups/profiles they provision."],
  },
];

function usageHint(usage) {
  if (!usage) return null;
  const parts = [
    ["permissions", "permissions"], ["das", "data access sets"], ["securityFilters", "security filters"],
    ["contentPackages", "content packages"], ["capabilities", "capabilities"], ["profiles", "profiles"],
  ].filter(([k]) => usage[k]).map(([k, l]) => `${usage[k][0]} of ${usage[k][1]} ${l}`);
  return `Only artifacts in use are included (assigned to a user group, or provisioned by IDN): ${parts.join(", ")}.`;
}

function GuideView({ d, ix, nav, status }) {
  const stats = [
    ["User groups", Object.keys(d.groups).length, "group"],
    ["Permissions", Object.keys(d.permissions).length, "perm"],
    ["Data Access Sets", Object.keys(d.das).length, "das"],
    ["Security Filters", Object.keys(d.securityFilters).length, "sf"],
    ["Content Packages", Object.keys(d.contentPackages).length, "cp"],
    ["Guidebooks", Object.keys(ix.guidebooks).length, "guidebook"],
    ["Analyses", Object.values(ix.artifacts).filter(a => a.type === "analysis").length, "analysis"],
    ["IDN roles (Visier-related)", Object.keys(d.idn.roles).length, "role"],
  ];
  return (
    <div>
      <Header kind="Runbook" title="Visier Security Runbook"
        description={`Snapshot generated ${new Date(d.generatedAt).toLocaleString()}. Security configuration only — no user-level data.`} />
      <Block title="How Visier access is wired">
        <div className="vrb-flow">
          {["IDN Role (criteria / request)", "IDN Access Profile", "Visier User Group", "Permission",
            "Data Access Set · Security Filter · Capabilities · Content Package", "Guidebooks · Analyses · Metrics"].map((s, i, a) => (
            <React.Fragment key={s}>
              <div className="vrb-flow-step">{s}</div>
              {i < a.length - 1 && <div className="vrb-flow-arrow">→</div>}
            </React.Fragment>
          ))}
        </div>
      </Block>
      <Block title="What's in this snapshot"
        hint={usageHint(d.usage)}>
        <div className="vrb-stats">
          {stats.map(([l, n, s]) => (
            <button key={l} type="button" className="vrb-stat" onClick={() => nav({ section: s })}>
              <div className="vrb-stat-n">{n}</div><div className="vrb-stat-l">{l}</div>
            </button>
          ))}
        </div>
      </Block>
      <Block title="Answering common questions">
        {RECIPES.map(r => (
          <details key={r.q} className="vrb-card vrb-details" open>
            <summary className="vrb-card-head"><b>{r.q}</b></summary>
            <ol className="vrb-steps">{r.steps.map((s, i) => <li key={i}>{s}</li>)}</ol>
          </details>
        ))}
      </Block>
    </div>
  );
}

function sectionItems(d, ix, section) {
  const v = o => Object.values(o);
  switch (section) {
    case "group": return v(d.groups).sort(byName).map(x => ({ type: "group", id: x.id, label: x.name, sub: x.layer === "NonVersioned" ? "Production" : "Versioned" }));
    case "perm": return v(d.permissions).sort(byName).map(x => ({ type: "perm", id: x.id, label: x.name, sub: `${(ix.permToGroups[x.id] || []).length} groups` }));
    case "das": return v(d.das).sort(byName).map(x => ({ type: "das", id: x.id, label: x.name, sub: x.object }));
    case "sf": return v(d.securityFilters).sort(byName).map(x => ({ type: "sf", id: x.id, label: x.name, sub: x.object }));
    case "cap": return Object.entries(d.capabilities).map(([k, [dn]]) => ({ type: "cap", id: k, label: dn, sub: `${(ix.capToPerms[k] || []).length} permissions` })).sort((a, b) => a.label.localeCompare(b.label));
    case "cp": return v(d.contentPackages).sort(byName).map(x => ({ type: "cp", id: x.id, label: x.name, sub: `${(ix.cpToPerms[x.id] || []).length} permissions` }));
    case "guidebook": return v(ix.guidebooks).sort(byName).map(x => ({ type: "guidebook", id: x.name, label: x.name, sub: `${x.topics.length} topics` }));
    case "analysis": return v(ix.artifacts).filter(a => a.type === "analysis").sort(byName).map(x => ({ type: "artifact", id: x.key, label: x.name, sub: `${x.cps.length} packages` }));
    case "role": return v(d.idn.roles).sort(byName).map(x => ({ type: "role", id: x.id, label: x.name, sub: x.enabled ? (x.criteria ? "Dynamic" : x.requestable ? "Requestable" : "Manual") : "Disabled" }));
    case "ap": return v(d.idn.accessProfiles).sort(byName).map(x => ({ type: "ap", id: x.id, label: x.name, sub: x.requestable ? "Requestable" : "" }));
    case "profile": return v(d.profiles).sort(byName).map(x => ({ type: "profile", id: x.id, label: x.name, sub: ix.entByValue[x.id] ? "IDN" : "" }));
    default: return [];
  }
}

function SearchResults({ items, query, nav, current }) {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  const hits = items.filter(it => terms.every(t => it.l.includes(t)));
  const groups = {};
  hits.forEach(h => { (groups[h.type] = groups[h.type] || []).push(h); });
  const order = ["group", "perm", "das", "sf", "cap", "cp", "guidebook", "artifact", "prop", "role", "ap", "profile"];
  if (!hits.length) return <Empty>No matches for “{query}”.</Empty>;
  return order.filter(t => groups[t]).map(t => (
    <div key={t} className="vrb-search-group">
      <div className="vrb-search-type">{TYPE_LABELS[t]} <span className="vrb-count">{groups[t].length}</span></div>
      {groups[t].slice(0, 40).map(h => (
        <ListItem key={h.type + h.id} item={h} nav={nav} active={current?.type === h.type && current?.id === h.id} />
      ))}
      {groups[t].length > 40 && <div className="vrb-muted vrb-more">+{groups[t].length - 40} more — refine the search</div>}
    </div>
  ));
}

function ListItem({ item, nav, active }) {
  return (
    <button type="button" className={`vrb-li ${active ? "active" : ""}`} onClick={() => nav({ type: item.type, id: item.id })}>
      <span className="vrb-li-label">{item.label}</span>
      {item.sub && <span className="vrb-li-sub">{item.sub}</span>}
    </button>
  );
}

const VIEWS = {
  group: GroupView, perm: PermView, das: DasView, sf: SfView, cap: CapView, cp: CpView,
  guidebook: GuidebookView, artifact: ArtifactView, prop: PropView, role: RoleView, ap: ApView, profile: ProfileView,
};

function exists(d, ix, v) {
  switch (v.type) {
    case "group": return !!d.groups[v.id];
    case "perm": return !!d.permissions[v.id];
    case "das": return !!d.das[v.id];
    case "sf": return !!d.securityFilters[v.id];
    case "cap": return !!d.capabilities[v.id] || !!ix.capToPerms[v.id];
    case "cp": return !!d.contentPackages[v.id];
    case "guidebook": return !!ix.guidebooks[v.id];
    case "artifact": return !!ix.artifacts[v.id];
    case "prop": return !!ix.propIndex[v.id];
    case "role": return !!d.idn.roles[v.id];
    case "ap": return !!d.idn.accessProfiles[v.id];
    case "profile": return !!d.profiles[v.id];
    default: return false;
  }
}

// ── Main ─────────────────────────────────────────────────────────────────────

export function RunbookExplorer({ data, status }) {
  const ix = useMemo(() => buildIndex(data), [data]);
  const searchItems = useMemo(() => buildSearchItems(data, ix), [data, ix]);
  const [section, setSection] = useState("guide");
  const [stack, setStack] = useState([{ type: "guide" }]);
  const [query, setQuery] = useState("");
  const [listFilter, setListFilter] = useState("");
  const current = stack[stack.length - 1];

  const nav = useCallback(to => {
    if (to.section) {
      setSection(to.section);
      setListFilter("");
      if (to.section === "guide") setStack(s => [...s, { type: "guide" }]);
      return;
    }
    setStack(s => [...s, to]);
  }, []);
  const back = () => setStack(s => (s.length > 1 ? s.slice(0, -1) : s));

  const items = useMemo(() => sectionItems(data, ix, section), [data, ix, section]);
  const lf = listFilter.toLowerCase();
  const shown = lf ? items.filter(i => (i.label + " " + (i.sub || "")).toLowerCase().includes(lf)) : items;

  let detail;
  if (current.type === "guide") detail = <GuideView d={data} ix={ix} nav={nav} status={status} />;
  else if (!exists(data, ix, current)) detail = <Empty>This item is not in the current snapshot.</Empty>;
  else {
    const View = VIEWS[current.type];
    detail = <View key={current.type + current.id} d={data} ix={ix} nav={nav} id={current.id} />;
  }

  return (
    <div className="vrb-root">
      <div className="vrb-searchbar">
        <input className="cug-input" value={query} onChange={e => setQuery(e.target.value)}
          placeholder="Search everything — group, permission, DAS, capability, guidebook, analysis, field, IDN role…" />
        {query && <button type="button" className="compare-btn" onClick={() => setQuery("")}>Clear</button>}
      </div>
      <div className="vrb-layout">
        <aside className="vrb-side">
          {query.trim() ? (
            <SearchResults items={searchItems} query={query.trim()} nav={nav} current={current} />
          ) : (
            <>
              <div className="vrb-sections">
                {SECTIONS.map(s => (
                  <button key={s.id} type="button" className={`vrb-sec ${section === s.id ? "active" : ""}`}
                    onClick={() => nav({ section: s.id })}>{s.label}</button>
                ))}
              </div>
              {section !== "guide" && (
                <>
                  <input className="cug-input vrb-filter" value={listFilter} onChange={e => setListFilter(e.target.value)} placeholder="Filter list…" />
                  <div className="vrb-filter-meta">{shown.length} of {items.length}</div>
                  <div className="vrb-list">
                    {shown.slice(0, LIST_LIMIT).map(it => (
                      <ListItem key={it.type + it.id} item={it} nav={nav} active={current.type === it.type && current.id === it.id} />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </aside>
        <main className="vrb-main">
          <div className="vrb-crumbs">
            <button type="button" className="compare-btn" disabled={stack.length <= 1} onClick={back}>← Back</button>
            <button type="button" className="compare-btn" onClick={() => nav({ type: "guide" })}>Runbook Guide</button>
            {current.type !== "guide" && <span className="vrb-muted">{TYPE_LABELS[current.type]}</span>}
          </div>
          {detail}
        </main>
      </div>
    </div>
  );
}

async function apiJson(path, init) {
  const res = await fetch(`${API_BASE}${path}`, init);
  const text = await res.text();
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch { /* non-JSON error */ }
  if (!res.ok) throw new Error(body?.error || text || `API error ${res.status}`);
  return body;
}

export default function VisierRunbook({ canRebuild = false }) {
  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [polling, setPolling] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const meta = await apiJson(`/visier-runbook?_=${Date.now()}`);
      setStatus(meta.status);
      if (meta.status?.state === "running") setPolling(true);
      if (meta.downloadUrl) {
        const res = await fetch(meta.downloadUrl);
        if (!res.ok) throw new Error(`Snapshot download failed (${res.status})`);
        setData(await res.json());
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!polling) return undefined;
    const t = setInterval(async () => {
      try {
        const meta = await apiJson(`/visier-runbook?_=${Date.now()}`);
        setStatus(meta.status);
        if (meta.status?.state !== "running") {
          setPolling(false);
          if (meta.status?.state === "succeeded") load();
        }
      } catch (e) {
        setPolling(false);
        setError(e.message);
      }
    }, 10000);
    return () => clearInterval(t);
  }, [polling, load]);

  const rebuild = async () => {
    setError(null);
    try {
      const r = await apiJson("/visier-runbook", { method: "POST" });
      setStatus(r.status);
      if (r.status?.state === "succeeded") load();
      else setPolling(true);
    } catch (e) {
      // A synchronous rebuild outlives API Gateway's 29s limit (504 / CORS-less failure);
      // check the status before treating it as an error.
      try {
        const meta = await apiJson(`/visier-runbook?_=${Date.now()}`);
        setStatus(meta.status);
        if (meta.status?.state === "running") { setPolling(true); return; }
      } catch { /* fall through */ }
      setError(e.message);
    }
  };

  const running = status?.state === "running";
  const ageHours = data ? (Date.now() - new Date(data.generatedAt).getTime()) / 36e5 : 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div className="vrb-toolbar">
        <div className="vrb-muted">
          {data && <>Snapshot: <b>{new Date(data.generatedAt).toLocaleString()}</b> · refreshes automatically every day</>}
          {data && ageHours > 36 && !running && <span style={{ color: "#f0d48a" }}> · older than a day — the daily refresh may have failed</span>}
          {running && <> · <span className="spinner" style={{ marginRight: 6 }} />Rebuilding from Visier + IdentityNow (≈3 min)…</>}
          {status?.state === "failed" && <span style={{ color: "var(--red)" }}> · Last rebuild failed: {status.error}</span>}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="compare-btn" onClick={load} disabled={loading}>Reload</button>
          {canRebuild && (
            <button type="button" className="compare-btn" onClick={rebuild} disabled={running}
              title="Optional: re-read Visier and IdentityNow now instead of waiting for the daily refresh">
              {running ? "Rebuilding…" : "Rebuild now"}
            </button>
          )}
        </div>
      </div>
      {error && <div className="error-banner"><strong>Error:</strong> {error}</div>}
      {loading && !data && (
        <div className="vrb-empty"><span className="spinner" style={{ marginRight: 8 }} />Loading Visier security snapshot…</div>
      )}
      {!loading && !data && !error && (
        <div className="vrb-empty">No snapshot yet. It is generated by the daily refresh{canRebuild ? <>, or click <b>Rebuild now</b></> : null}.</div>
      )}
      {data && <RunbookExplorer data={data} status={status} />}
      <style>{RUNBOOK_CSS}</style>
    </div>
  );
}

const RUNBOOK_CSS = `
.vrb-toolbar { display:flex; justify-content:space-between; align-items:center; gap:12px; flex-wrap:wrap; }
.vrb-root { display:flex; flex-direction:column; gap:14px; }
.vrb-searchbar { display:flex; gap:10px; }
.vrb-searchbar .cug-input { flex:1; }
.vrb-layout { display:grid; grid-template-columns: 320px 1fr; gap:18px; align-items:start; }
.vrb-side { position:sticky; top:12px; max-height: calc(100vh - 160px); overflow:auto; border:1px solid var(--border); border-radius:var(--radius-md); background:var(--bg-surface); padding:10px; }
.vrb-main { min-width:0; }
.vrb-sections { display:flex; flex-wrap:wrap; gap:6px; margin-bottom:10px; }
.vrb-sec, .vrb-pill { background:var(--bg-raised); border:1px solid var(--border); color:var(--text-secondary); border-radius:999px; padding:4px 10px; font-size:12px; cursor:pointer; }
.vrb-sec.active, .vrb-pill.active { background:var(--green-dim); border-color:var(--green); color:var(--green-bright); }
.vrb-pills { display:flex; flex-wrap:wrap; gap:6px; margin-bottom:10px; }
.vrb-filter { margin-bottom:4px; }
.vrb-filter-meta { font-size:11px; color:var(--text-muted); margin:2px 0 8px; }
.vrb-list { display:flex; flex-direction:column; }
.vrb-li { display:flex; flex-direction:column; align-items:flex-start; text-align:left; background:none; border:none; border-radius:var(--radius-sm); padding:6px 8px; color:var(--text-primary); cursor:pointer; width:100%; }
.vrb-li:hover { background:var(--bg-hover); }
.vrb-li.active { background:var(--green-dim); }
.vrb-li-label { font-size:13px; word-break:break-word; }
.vrb-li-sub { font-size:11px; color:var(--text-muted); }
.vrb-search-group { margin-bottom:10px; }
.vrb-search-type { font-size:10px; text-transform:uppercase; letter-spacing:.07em; color:#8ba2b8; margin:6px 0 2px; }
.vrb-more { font-size:11px; padding:4px 8px; }
.vrb-crumbs { display:flex; gap:8px; align-items:center; margin-bottom:12px; }
.vrb-header { border:1px solid var(--border); border-radius:var(--radius-md); background:var(--bg-card); padding:16px 18px; margin-bottom:14px; }
.vrb-kind { font-size:10px; text-transform:uppercase; letter-spacing:.08em; color:var(--text-muted); }
.vrb-title { margin:4px 0 8px; font-size:20px; color:var(--text-primary); word-break:break-word; }
.vrb-badges { display:flex; flex-wrap:wrap; gap:6px; }
.vrb-desc { color:var(--text-secondary); font-size:13px; margin-top:8px; white-space:pre-wrap; }
.vrb-id { font-size:11px; color:var(--text-muted); margin-top:8px; }
.vrb-block { border:1px solid var(--border); border-radius:var(--radius-md); background:var(--bg-surface); padding:14px 16px; margin-bottom:14px; }
.vrb-block-title { font-size:13px; font-weight:700; color:var(--text-primary); margin-bottom:10px; display:flex; align-items:center; gap:8px; }
.vrb-count { background:var(--bg-raised); border:1px solid var(--border); border-radius:999px; padding:0 7px; font-size:11px; color:var(--text-secondary); font-weight:600; }
.vrb-hint { font-size:12px; color:var(--text-muted); margin:-4px 0 10px; }
.vrb-sub { font-size:10px; text-transform:uppercase; letter-spacing:.07em; color:#8ba2b8; margin:14px 0 6px; }
.vrb-empty { color:var(--text-muted); font-size:13px; padding:8px 0; }
.vrb-muted { color:var(--text-muted); font-size:12px; }
.vrb-warn { border:1px solid #8a6d1f; background:rgba(184,140,40,.12); color:#f0d48a; border-radius:var(--radius-sm); padding:8px 10px; font-size:13px; margin:8px 0; }
.vrb-link { background:none; border:none; padding:0; color:var(--green-bright); cursor:pointer; font:inherit; text-align:left; text-decoration:underline; text-decoration-color:rgba(131,222,169,.35); text-underline-offset:2px; }
.vrb-link:hover { color:#fff; }
.vrb-chips { display:flex; flex-wrap:wrap; gap:6px 14px; font-size:13px; }
.vrb-badge { display:inline-block; font-size:11px; border-radius:999px; padding:1px 8px; border:1px solid var(--border); white-space:nowrap; }
.vrb-badge-muted { color:var(--text-muted); }
.vrb-badge-green { color:var(--green-bright); border-color:var(--green); background:var(--green-dim); }
.vrb-badge-blue { color:#7fd0f5; border-color:#2b7aa6; background:var(--blue-dim); }
.vrb-badge-teal { color:#7fe0c4; border-color:#2d8a70; background:var(--teal-dim); }
.vrb-badge-amber { color:#f0d48a; border-color:#8a6d1f; background:rgba(184,140,40,.12); }
.vrb-badge-red { color:#ff9a94; border-color:var(--red); background:var(--red-dim); }
.vrb-badge-purple { color:#d3b8ff; border-color:#7a5bb5; background:rgba(122,91,181,.15); }
.vrb-card { border:1px solid var(--border); border-radius:var(--radius-sm); background:var(--bg-card); padding:10px 12px; margin:6px 0; }
.vrb-card-ent { border-color:#2d8a70; }
.vrb-card-head { display:flex; flex-wrap:wrap; align-items:center; gap:8px; font-size:13px; cursor:default; }
details > summary.vrb-card-head { cursor:pointer; }
.vrb-details > summary { cursor:pointer; }
.vrb-details { margin:6px 0; }
.vrb-kv { display:grid; grid-template-columns: 150px 1fr; gap:4px 12px; font-size:12px; margin-top:8px; }
.vrb-kv > span:nth-child(odd) { color:var(--text-muted); }
.vrb-kv-wide { grid-template-columns: 200px 1fr; margin-bottom:10px; }
.vrb-chain { margin-left:14px; border-left:2px solid var(--border); padding-left:12px; }
.vrb-nested { margin-top:6px; }
.vrb-arrow { font-size:11px; color:var(--text-muted); margin-top:8px; }
.vrb-crit-group { border-left:2px solid #2b7aa6; padding-left:10px; margin:8px 0 4px; }
.vrb-crit-op { font-size:11px; color:#7fd0f5; font-weight:700; margin-bottom:4px; }
.vrb-crit-join { font-size:10px; color:#7fd0f5; margin:2px 0; font-weight:700; }
.vrb-crit-leaf { display:flex; flex-wrap:wrap; gap:6px 10px; align-items:center; background:var(--bg-raised); border:1px solid var(--border); border-radius:var(--radius-sm); padding:6px 8px; font-size:12px; }
.vrb-crit-type { color:var(--text-muted); min-width:110px; }
.vrb-crit-src { color:#7fe0c4; }
.vrb-crit-attr { color:var(--text-secondary); font-family:var(--font-mono); }
.vrb-crit-opname { color:#f0d48a; }
.vrb-crit-vals { display:flex; flex-wrap:wrap; gap:6px; }
.vrb-val { font-family:var(--font-mono); font-size:12px; background:var(--bg-base); border:1px solid var(--border); border-radius:4px; padding:1px 6px; color:var(--text-primary); }
.vrb-table { border:1px solid var(--border); border-radius:var(--radius-sm); overflow:hidden; font-size:12px; }
.vrb-tr { display:grid; grid-template-columns: 1fr 1.3fr 1.3fr; gap:10px; padding:8px 10px; border-bottom:1px solid var(--border); align-items:start; }
.vrb-tr:last-child { border-bottom:none; }
.vrb-tr-2 { grid-template-columns: 1fr 1.6fr; }
.vrb-tr-4 { grid-template-columns: 1.2fr 1.2fr 1fr 110px; }
.vrb-th { background:var(--bg-raised); font-size:10px; text-transform:uppercase; letter-spacing:.06em; color:#8ba2b8; }
.vrb-row { padding:4px 0; font-size:13px; border-bottom:1px dashed rgba(57,80,97,.6); }
.vrb-prop-row { display:grid; grid-template-columns: 1.4fr 1fr 100px; gap:10px; padding:4px 0; font-size:12px; border-bottom:1px dashed rgba(57,80,97,.6); align-items:center; }
.vrb-pre { font-family:var(--font-mono); font-size:11px; background:var(--bg-base); border:1px solid var(--border); border-radius:4px; padding:8px; overflow:auto; max-height:260px; white-space:pre; }
.vrb-flow { display:flex; flex-wrap:wrap; align-items:center; gap:8px; }
.vrb-flow-step { background:var(--bg-raised); border:1px solid var(--border); border-radius:var(--radius-sm); padding:8px 10px; font-size:12px; color:var(--text-primary); }
.vrb-flow-arrow { color:var(--green); font-weight:700; }
.vrb-stats { display:grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap:10px; }
.vrb-stat { background:var(--bg-raised); border:1px solid var(--border); border-radius:var(--radius-sm); padding:10px; text-align:left; cursor:pointer; color:inherit; }
.vrb-stat:hover { border-color:var(--green); }
.vrb-stat-n { font-size:22px; font-weight:700; color:var(--green-bright); }
.vrb-stat-l { font-size:12px; color:var(--text-secondary); }
.vrb-steps { margin:8px 0 2px 18px; font-size:13px; color:var(--text-secondary); line-height:1.55; }
@media (max-width: 1100px) { .vrb-layout { grid-template-columns: 1fr; } .vrb-side { position:static; max-height:420px; } }
`;
